import type { FastCheckoutMethod } from '@design-system/composables/useFastCheckoutMethods'
import { useExpressCheckoutVisibility } from '@design-system/composables/useExpressCheckoutVisibility'
import { getPaidSkuLineItems } from '@design-system/utils/paidSkuLineItems'
import { IS_PDP_FAST_CHECKOUT_ENABLED } from '@design-system/data/fastCheckout'

export type FastCheckoutIntent =
  | { mode: 'method'; method: FastCheckoutMethod }
  | { mode: 'chooser' }
  | null

/**
 * PDP fast-checkout session: which CTA the size selector should show, and
 * whether the isolated express order is ready after a size pick.
 *
 * - `null` — size selector keeps Add to bag
 * - `{ mode: 'method', method }` — footer is that wallet
 * - `{ mode: 'chooser' }` — footer is the Fast checkout logos row
 *
 * @returns Intent state, setters, preparing flag and prepared order, OOB and PDP block visibility, and whether the express wallet can mount.
 */
export const useFastCheckoutIntent = () => {
  const { addToCartPayload } = useAddToCart()
  const { order } = useExpressOrder()
  const { isHiddenForOob } = useExpressCheckoutVisibility()

  const isPdpFastCheckoutHidden = computed(
    () => !IS_PDP_FAST_CHECKOUT_ENABLED || isHiddenForOob.value
  )

  const intent = useState<FastCheckoutIntent>(
    'pdp-fast-checkout-intent',
    () => null
  )
  const isPreparing = useState('pdp-fast-checkout-preparing', () => false)
  /** Express order a PDP size has already been fully prepared on. */
  const preparedOrderId = useState<string | null>(
    'pdp-fast-checkout-prepared-order-id',
    () => null
  )

  /**
   * The express order line item can differ from the PDP selection once it is
   * edited from the fast checkout layer, so readiness only needs a line item.
   *
   * Picking another size swaps the line item on the same order: the wallets
   * already mounted for it stay up while that happens (they read the new total
   * on click) instead of being torn down and rebuilt. Only the first size of an
   * order waits for the preparation to finish.
   */
  const isWalletReady = computed(() => {
    if (!addToCartPayload.value?.sku_code) return false
    if (isPreparing.value) {
      return !!order.value?.id && order.value.id === preparedOrderId.value
    }
    return getPaidSkuLineItems(order.value).length > 0
  })

  const setMethodIntent = (method: FastCheckoutMethod) => {
    intent.value = { mode: 'method', method }
  }

  const setChooserIntent = () => {
    intent.value = { mode: 'chooser' }
  }

  const clearIntent = () => {
    intent.value = null
  }

  return {
    intent,
    isPreparing,
    preparedOrderId,
    isWalletReady,
    isHiddenForOob,
    isPdpFastCheckoutHidden,
    setMethodIntent,
    setChooserIntent,
    clearIntent,
  }
}
